import React, { useEffect, useMemo, useCallback } from 'react'
import flowRight from 'lodash/flowRight'
import { useSelector } from 'react-redux'
import { organizationSettingSelector } from 'shared/hocs/withAuth'
import SelectionControlGroup from 'shared/components/SelectionGroupControl'
import withDialog, { WrappedProps } from 'shared/hocs/withDialog'
import { ConfirmationType } from 'shared/components/Notes/models/confirmation'
import TextField, { FieldLabel } from 'shared/components/TextField'
import styled from 'styled-components'
import CustomTooltip from 'shared/components/CustomTooltip'
import { FLAG_WARNING_COLOR, FLAG_DANGER_COLOR } from 'shared/constants/participant'
import Box from 'shared/components/Box'
import useQuery from 'shared/hooks/useQuery'
import queryString from 'qs'
import { NoteCategoryType } from 'pages/CustomizeScoreboard/model/noteCategory'
import SelectAutocompleteCheckbox from 'shared/components/SelectAutocompleteCheckbox'

type ControlOptionsProps = {
  label: string
  value: string
}

type Props = WrappedProps<ConfirmationType> & {
  label?: string
  description?: string
  isFlag?: boolean
  type: 'stop' | 'warn'
}

function FlagActionConfirmationDialog(props: Props) {
  const { formState, formHandlers, label = 'Message', description = '', type = 'warn', isFlag = false } = props
  const { fields, errors } = formState
  const { onElementChange, onChange } = formHandlers
  const { organization_information } = useSelector(organizationSettingSelector)

  const noteCategoryQueryString = queryString.stringify({
    sort: [{ column: 'name', direction: 'asc' }],
  })
  const [noteCategoryQueryState] = useQuery<NoteCategoryType[]>(
    {
      url: `/note-category?${noteCategoryQueryString}`,
    },
    { isBase: true, initialData: [], skip: false },
  )

  const noteCategoryOptions = useMemo(() => noteCategoryQueryState.data.map(e => ({ label: e.name, value: e.name })), [
    noteCategoryQueryState.data,
  ])

  const optionControls = useMemo(() => {
    const options: ControlOptionsProps[] = []
    if (type === 'warn')
      options.push({
        label: 'Reminder (Member can still check-in)',
        value: 'warn',
      })
    else
      options.push({
        label: 'Stop Member at Check-In',
        value: 'stop',
      })
    return options
  }, [type])

  const removeOrAddToList = useCallback(
    (checked: boolean, method: string) => {
      return checked ? fields?.categories?.concat([method]) : fields?.categories?.filter(e => e !== method)
    },
    [fields],
  )

  useEffect(() => {
    onElementChange(type, 'type')
  }, [type])

  return (
    <FlagActionConfirmationDialogBodyContainer className="confirm-dialog-container">
      <Title mb="20px">
        Flag This Member{' '}
        <CustomTooltip
          placement="bottom"
          className="tooltip_flag"
          width="25"
          height="24"
          toopTipMaxWidth="380px"
          title="These selections will be present on Check-In/Out and Attendance List. Selections will be always shown until manually removed."
        />
      </Title>
      <Box mb="10px">
        <FieldLabel mb="10px !important">{type == 'warn' ? 'Yellow' : 'Red'} Status</FieldLabel>
        <Box>
          <SelectionControlGroup
            id="type"
            name="type"
            className={`flag-type-selection ${type == 'warn' ? 'warn' : 'stop'}`}
            controls={optionControls}
            value={fields.type}
            onChange={val => {
              onElementChange(val, 'type')
            }}
            type="radio"
          />
        </Box>
      </Box>
      <Box>
        <FieldLabel mb="10px !important">Note*</FieldLabel>
        <TextField
          id="message"
          placeholder="Write your reason here"
          maxRows={3}
          rows={3}
          value={fields.message}
          onChange={onElementChange}
          error={Boolean(errors.message)}
          errorText={errors.message}
          disabled={false}
        />
      </Box>
      {organization_information.features.note.is_enabled && (
        <Box mt="24px">
          <FieldLabel>Category</FieldLabel>
          <SelectAutocompleteCheckbox
            options={noteCategoryOptions}
            selected={fields?.categories || []}
            node="categories"
            onChange={onChange}
            removeOrAddToList={removeOrAddToList}
          ></SelectAutocompleteCheckbox>
        </Box>
      )}
    </FlagActionConfirmationDialogBodyContainer>
  )
}

const WrappedFlagActionConfirmationDialog = flowRight(withDialog())(FlagActionConfirmationDialog)

const Title = styled(Box)`
  line-height: 24px;
  font-size: 20px;
  font-weight: 700;
  display: flex;

  .tooltip_flag {
    margin-left: 5px;
  }
`

const FlagActionConfirmationDialogBodyContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.secondary};
  padding: 40px 40px 0;
  width: 100%;
  max-width: 540px;

  .styled-tooltip {
    max-width: 550px;
  }

  .description {
    width: 100%;
    margin-bottom: 40px;
    text-align: center;

    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
  }

  .flag-type-selection {
    flex-direction: column;

    &.warn {
      .md-selection-control-container {
        border-left: 10px solid ${FLAG_WARNING_COLOR};
      }
    }

    &.stop {
      .md-selection-control-container {
        border-left: 10px solid ${FLAG_DANGER_COLOR};
      }
    }

    .md-selection-control-container {
      margin-bottom: 10px;
      padding: 15px 20px;
      background-color: ${({ theme }) => `${theme.colors.border}25`};
      border-radius: 5px;
      border: ${({ theme }) => `1px solid ${theme.colors.border}`};
      height: 44px;
      width: 100%;
      display: flex;
    }
  }
`

WrappedFlagActionConfirmationDialog.defaultProps = {
  dialogClassName: 'dialog--large',
  continueIsDisabled(data: ConfirmationType) {
    return !data?.message?.length
  },
}

export default WrappedFlagActionConfirmationDialog
